/**
 * AUT: Aceptar Invitación
 * Lee token de los query params y permite al usuario crear su contraseña inicial.
 */
import { useEffect, useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react'
import { useAcceptInvitation } from '@/modules/auth/hooks/useAuth'
import { Button } from '@/components/Button'

const schema = z.object({
  password: z.string()
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .regex(/[A-Z]/, 'Debe contener al menos una letra mayúscula')
    .regex(/[a-z]/, 'Debe contener al menos una letra minúscula')
    .regex(/[0-9]/, 'Debe contener al menos un número')
    .regex(/[!@#$%^&*(),.?":{}|<>\-_+=[\]~`/\\]/, 'Debe contener al menos un carácter especial'),
  confirm_password: z.string().min(1, 'Confirma tu contraseña'),
}).refine(d => d.password === d.confirm_password, {
  message: 'Las contraseñas no coinciden',
  path: ['confirm_password'],
})
type FormData = z.infer<typeof schema>

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const [showPass, setShowPass] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [success, setSuccess] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  const { mutate: acceptInvitation, isPending } = useAcceptInvitation()

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => navigate('/auth/login'), 2000)
      return () => clearTimeout(timer)
    }
  }, [success, navigate])

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-auth px-4">
        <div className="w-full max-w-md">
          <div className="bg-surface rounded-2xl shadow-2xl p-8 text-center">
            <div className="text-4xl mb-3">⚠️</div>
            <h2 className="text-xl font-semibold text-content mb-2">Enlace inválido</h2>
            <p className="text-sm text-content-muted mb-6">
              Este enlace de invitación no es válido o ha expirado.
            </p>
            <Link
              to="/auth/login"
              className="inline-block px-6 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover transition-colors"
            >
              Ir al inicio de sesión
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const onSubmit = (data: FormData) => {
    setApiError(null)
    acceptInvitation({ token, new_password: data.password }, {
      onSuccess: () => setSuccess(true),
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string }; detail?: string } } }
        setApiError(
          err.response?.data?.error?.message ||
          err.response?.data?.detail ||
          'Error al activar la cuenta. El enlace puede haber expirado.'
        )
      },
    })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-auth px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/clobi-logo-white.svg" alt="Clobi" className="h-32 mx-auto mb-4" />
          <p className="text-content-subtle mt-1 text-sm">Renace tu empresa. Opera en la nube.</p>
        </div>

        <div className="bg-surface rounded-2xl shadow-2xl p-8">
          {success ? (
            <div className="text-center py-4">
              <CheckCircle2 size={48} className="mx-auto text-success-solid mb-4" />
              <h2 className="text-xl font-semibold text-content mb-2">¡Bienvenido a Clobi!</h2>
              <p className="text-sm text-content-muted">Tu cuenta está activa. Redirigiendo al inicio de sesión...</p>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-content mb-2">Activar cuenta</h2>
              <p className="text-sm text-content-muted mb-6">
                Has sido invitado a Clobi. Crea tu contraseña para activar tu cuenta.
              </p>

              {apiError && (
                <div className="mb-4 p-3 rounded-lg bg-danger-surface border border-danger-border text-danger-content text-sm">
                  ⚠️ {apiError}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
                <div>
                  <label htmlFor="acceptinvitationpage-password" className="block text-sm font-medium text-content mb-1.5">
                    Contraseña
                  </label>
                  <div className="relative">
                    <input id="acceptinvitationpage-password"
                      {...register('password')}
                      type={showPass ? 'text' : 'password'}
                      autoFocus
                      placeholder="Mínimo 8 caracteres"
                      className={`w-full px-4 py-2.5 pr-10 rounded-lg border text-content placeholder:text-content-subtle
                        text-sm transition-colors outline-none focus:ring-2 focus:ring-primary focus:border-primary
                        ${errors.password ? 'border-danger-border-strong bg-danger-surface' : 'border-divider-strong'}`}
                    />
                    <button type="button" onClick={() => setShowPass(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted">
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && <p className="mt-1 text-xs text-danger-content-soft">{errors.password.message}</p>}
                </div>

                <div>
                  <label htmlFor="acceptinvitationpage-confirm-password" className="block text-sm font-medium text-content mb-1.5">
                    Confirmar contraseña
                  </label>
                  <div className="relative">
                    <input id="acceptinvitationpage-confirm-password"
                      {...register('confirm_password')}
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Repite la contraseña"
                      className={`w-full px-4 py-2.5 pr-10 rounded-lg border text-content placeholder:text-content-subtle
                        text-sm transition-colors outline-none focus:ring-2 focus:ring-primary focus:border-primary
                        ${errors.confirm_password ? 'border-danger-border-strong bg-danger-surface' : 'border-divider-strong'}`}
                    />
                    <button type="button" onClick={() => setShowConfirm(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted">
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.confirm_password && <p className="mt-1 text-xs text-danger-content-soft">{errors.confirm_password.message}</p>}
                </div>

                <Button type="submit" size="lg" fullWidth isPending={isPending} pendingLabel="Activando...">
                  Activar mi cuenta
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/auth/login" className="text-sm text-primary hover:text-primary-hover hover:underline">
                  ¿Ya tienes cuenta? Iniciar sesión
                </Link>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-content-muted text-xs mt-6">
          © 2026 Clobi by zen · Todos los derechos reservados
        </p>
      </div>
    </div>
  )
}
